import React, { useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaChevronRight } from "react-icons/fa";
import {
	IoIosArrowDown,
	IoIosArrowUp,
	IoIosClose,
	IoIosMenu,
} from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import { getApi } from "../../services/api";
import DetailCourseCard from "../common/cards/DetailCourseCard";
import { AuthContext } from "../../context/AuthContext";
import ProfileIcon from "../common/Header/ProfileIcon";
import { toast } from "react-toastify";
// import categories from "../../utils/categories.json";

function Navbar() {
	const [isVisible, setIsVisible] = useState(false);
	const [lastScrollY, setLastScrollY] = useState(0);
	const [thresholdReached, setThresholdReached] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isCoursesOpen, setIsCoursesOpen] = useState(false);
	const [CoursesMenu, setCoursesMenu] = useState(false);
	const [activeCategory, setActiveCategory] = useState("");
	const [courses, setCourses] = useState([]);
	const [categories, setCategories] = useState([]);

	const navigate = useNavigate();
	const { user } = useContext(AuthContext);
	const threshold = 15;
	const activationPoint = 200;

	const dropdownRef = useRef(null);
	const toggleRef = useRef(null);
	const location = useLocation();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [location]);

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const response = await getApi(`/courseCategory`);
				if (response && !response.error) {
					setCategories(response); // Set fetched categories
					// Set the first category as the active category if there are categories
					if (response.length > 0) {
						setActiveCategory(response[0].title);
					}
				} else {
					console.error(
						"Error fetching categories:",
						response.message || "Unknown error"
					);
				}
			} catch (error) {
				console.error("Error fetching categories:", error.message);
			}
		};

		fetchCategories();
	}, []);

	// Fetch courses based on the active category
	useEffect(() => {
		const fetchCourses = async () => {
			try {
				const response = await getApi(
					`/courses?filter[category]=${activeCategory}`
				);
				if (response && !response.error) {
					setCourses(response); // Set fetched courses
				} else {
					console.error(
						"Error fetching courses:",
						response.message || "Unknown error"
					);
				}
			} catch (error) {
				console.error("Error fetching courses:", error.message);
			}
		};

		if (activeCategory) {
			fetchCourses();
		}
	}, [activeCategory]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target) &&
				toggleRef.current &&
				!toggleRef.current.contains(event.target)
			) {
				setIsCoursesOpen(false);
				setIsMenuOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY;

			if (currentScrollY > activationPoint) {
				if (currentScrollY < lastScrollY) {
					setIsVisible(true);
					setThresholdReached(false);
				} else if (currentScrollY > lastScrollY + threshold) {
					setThresholdReached(true);
				}

				if (thresholdReached) {
					setIsVisible(false);
				}
			} else {
				setIsVisible(false);
			}

			setLastScrollY(currentScrollY);
		};

		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [lastScrollY, thresholdReached]);

	const toggleMenuDropdown = () => {
		setIsMenuOpen((prev) => !prev);
	};

	const pageNavigate = (path) => {
		if (
			user ||
			path === "auth" ||
			path === "about" ||
			path === "contact" ||
			path === "help" ||
			path === "howitworks" ||
			path === ""
		) {
			navigate(`/${path}`);
		} else {
			toast.info("Login to access the content");
			navigate(`/auth`);
		}
	};

	// Function to set the active category and reset courses
	const handleCategoryClick = (category) => {
		setActiveCategory(category.title);
	};

	return (
		<motion.div
			initial={{ y: -100 }}
			animate={{ y: isVisible ? 0 : -100 }}
			transition={{ duration: 0.5 }}
			className="fixed top-0 md:top-2 left-0 right-0 mx-auto md:w-8/12 w-full bg-black/40 backdrop-blur-md shadow-md md:rounded-3xl z-50 dynamic-text"
		>
			{/* Desktop Navbar */}
			<div className="hidden md:flex relative container mx-auto px-4 py-2 items-center justify-between">
				<div className="flex items-center space-x-6 text-sm font-medium">
					{/* Left Side Menu */}
					<div
						ref={toggleRef}
						onClick={() => pageNavigate("enroll")}
						className="bg-black px-4 py-2 rounded-full text-white flex items-center space-x-3 text-xs cursor-pointer"
					>
						<span>Courses</span>
						<FaChevronRight />
					</div>

					<button
						onClick={() => pageNavigate("about")}
						className="hover:underline focus:outline-none"
					>
						<span>About</span>
					</button>

					<button
						onClick={() => pageNavigate("contact")}
						className="hover:underline focus:outline-none"
					>
						<span>Contact</span>
					</button>
				</div>

				{/* Right Side Menu */}
				<div className="flex items-center space-x-6 text-sm font-medium">
					<button
						onClick={() => pageNavigate("help")}
						className="hover:underline focus:outline-none"
					>
						<span>Help</span>
					</button>

					<button
						onClick={() => pageNavigate("howitworks")}
						className="hover:underline focus:outline-none"
					>
						<span>How it works</span>
					</button>

					{user ? (
						<ProfileIcon />
					) : (
						<>
							<button
								onClick={() => pageNavigate("auth")}
								className="hover:underline focus:outline-none"
							>
								<span>Login</span>
							</button>

							<button
								onClick={() => pageNavigate("auth")}
								className="bg-black text-white text-xs px-6 py-2 rounded-full focus:outline-none"
							>
								<span>Signup</span>
							</button>
						</>
					)}
				</div>
			</div>

			{/* Mobile Navbar */}
			<div className="md:hidden relative flex items-center justify-end">
				<motion.div
					onClick={toggleMenuDropdown}
					className="px-4 py-2 text-4xl text-black cursor-pointer"
					initial={{ rotate: 0 }}
					animate={{ rotate: isMenuOpen ? 180 : 0 }}
					transition={{ duration: 0.3 }}
				>
					{isMenuOpen ? <IoIosClose /> : <IoIosMenu />}
				</motion.div>

				<AnimatePresence>
					{isMenuOpen && (
						<motion.div
							ref={dropdownRef}
							key="menuDropdown"
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "95vh" }}
							exit={{ opacity: 0, height: 0 }}
							transition={{ duration: 0.3 }}
							className="absolute top-12 left-0 right-0 mx-auto w-full bg-white flex flex-col items-center space-y-4 p-6 z-50 overflow-y-auto no-scrollbar"
						>
							<div className="py-8 flex flex-col space-y-6 w-full text-sm">
								<div
									className="px-5 py-3 bg-black text-white font-medium border border-gray-500 rounded-xl flex justify-between items-center cursor-pointer"
									onClick={() => pageNavigate("enroll")}
								>
									<button>Courses</button>
									<FaChevronRight />
								</div>

								<div
									className="px-5 py-3 font-medium border border-gray-500 rounded-xl"
									onClick={() => pageNavigate("about")}
								>
									<button>About</button>
								</div>

								<div
									className="px-5 py-3 font-medium border border-gray-500 rounded-xl"
									onClick={() => pageNavigate("contact")}
								>
									<button>Contact</button>
								</div>

								<div
									className="px-5 py-3 font-medium border border-gray-500 rounded-xl"
									onClick={() => pageNavigate("help")}
								>
									<button>Help</button>
								</div>

								<div
									className="px-5 py-3 font-medium border border-gray-500 rounded-xl"
									onClick={() => pageNavigate("howitworks")}
								>
									<button>How it works</button>
								</div>

								{user ? (
									<div className="flex gap-6 justify-center items-center py-10 bg-gray-50">
										<div className="flex flex-col items-center px-6 py-4">
											<ProfileIcon />
											<div className="text-gray-800 font-semibold text-sm mt-3">
												{user.fullName}
											</div>
										</div>
									</div>
								) : (
									<div className="flex gap-4 justify-center items-center py-10 font-medium">
										<div
											className="px-5 py-3 border border-gray-500 rounded-xl"
											onClick={() => pageNavigate("auth")}
										>
											<button>Login</button>
										</div>

										<div
											className="px-5 py-3 bg-black text-white rounded-xl"
											onClick={() => pageNavigate("auth")}
										>
											<button>Signup</button>
										</div>
									</div>
								)}
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</motion.div>
	);
}

export default Navbar;
